import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSales } from '@/hooks/useSales';
import { ProductDemandAnalysis } from '@/components/analytics/ProductDemandAnalysis';

interface StockLevelChartProps {
  onInventoryClick?: () => void;
  onLowStockClick?: () => void;
  showProductAnalysis?: boolean;
}

interface StockTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const StockTooltip: React.FC<StockTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground">
          Stock: <span className="font-medium text-foreground">{data.quantity}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          Minimum: <span className="font-medium text-foreground">{data.minThreshold}</span>
        </p>
        {data.isLowStock && (
          <p className="text-sm text-red-600 font-medium">⚠️ Stock faible</p>
        )}
      </div>
    );
  }
  return null;
};

export const StockLevelChart: React.FC<StockLevelChartProps> = ({
  onInventoryClick,
  onLowStockClick,
  showProductAnalysis = false
}) => {
  const { products } = useSupabaseInventory();
  const { sales } = useSales();
  const isMobile = useIsMobile();
  const [stockLimit, setStockLimit] = useState(isMobile ? 10 : 20);
  const [sortBy, setSortBy] = useState<'stock-asc' | 'stock-desc' | 'name'>('stock-asc');
  
  const stockChartData = useMemo(() => {
    const sortedProducts = [...products].sort((a, b) => {
      switch (sortBy) {
        case 'stock-asc':
          return a.quantity - b.quantity;
        case 'stock-desc':
          return b.quantity - a.quantity;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    
    return sortedProducts
      .slice(0, stockLimit)
      .map(product => ({
        name: product.name.length > 15 ? `${product.name.substring(0, 15)}...` : product.name,
        quantity: product.quantity,
        minThreshold: product.min_threshold,
        isLowStock: product.quantity <= product.min_threshold && product.min_threshold > 0
      }));
  }, [products, stockLimit, sortBy]);

  const lowStockCount = products.filter(p => p.quantity <= p.min_threshold && p.min_threshold > 0).length;

  return (
    <div className="space-y-6">
      <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/20 group-hover:from-blue-500/20 group-hover:to-blue-600/30 transition-transform duration-200 hover:scale-105">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1 text-blue-700 dark:text-blue-300">
                  Stock management
                </p>
                <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                  Niveaux de stock par produit
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'stock-asc' | 'stock-desc' | 'name')}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Trier par" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock-asc">Stock croissant</SelectItem>
                    <SelectItem value="stock-desc">Stock décroissant</SelectItem>
                    <SelectItem value="name">Nom A-Z</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={String(stockLimit)} onValueChange={v => setStockLimit(Number(v))}>
                  <SelectTrigger className="w-[110px]">
                    <SelectValue placeholder="Limite" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {lowStockCount > 0 && (
                <button
                  onClick={onLowStockClick}
                  className="text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                >
                  ⚠️ {lowStockCount} en rupture
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="h-56 sm:h-72 lg:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stockChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-35} 
                textAnchor="end" 
                height={80} 
                tickFormatter={value => value.length > 12 ? value.substring(0, 12) + "…" : value}
                tick={{
                  fontSize: 12,
                  fill: 'hsl(var(--muted-foreground))'
                }}
                interval={0} 
              />
              <YAxis />
              <Tooltip content={<StockTooltip />} />
              <Bar dataKey="quantity" radius={[6, 6, 0, 0]} barSize={20}>
                {stockChartData.map((entry, idx) => (
                  <Cell key={`c-${idx}`} fill={entry.isLowStock ? '#ef4444' : '#10b981'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {showProductAnalysis && (
        <ProductDemandAnalysis 
          sales={sales} 
          products={products} 
          customColor="#3b82f6" 
        />
      )}
    </div>
  );
};